/**
 * A thunderstorm: the window darkens to a stormy blue-grey, slanted rain falls
 * in three layers and splashes along the bottom edge, lightning strikes three
 * times with a flash over everything, then the rain thins and the sky clears.
 * With reduced motion there's far less rain, slower and straighter, and each
 * strike is one soft flash rather than a flicker.
 */
import { calm, rand, scene } from "./scene";

type Keys = ReadonlyArray<readonly [number, number]>;

const TOTAL_MS = 7500;
/** The sky darkens over this long, and clears from CLEAR_MS to the end. */
const DARKEN_MS = 800;
const CLEAR_MS = 5800;
/** Stormy blue-grey, darker up where the clouds are. */
const SKY_TOP = "rgba(14, 20, 32, 0.62)";
const SKY_BOTTOM = "rgba(26, 35, 52, 0.52)";

/** Rain builds over RAIN_IN and thins to nothing over RAIN_OUT (ms). */
const RAIN_IN = [150, 1300] as const;
const RAIN_OUT = [4900, 6300] as const;
/** Stragglers still falling once it's stopped fade out over the last stretch. */
const RAIN_FADE_MS = 900;
/** Pale enough to read on the darkened sky over either theme. */
const RAIN_COLOR = "rgb(218, 228, 244)";
/** Sideways drift per pixel of fall: the wind. It swings by GUST either way, about GUST_MS per radian. */
const SLANT = 0.26;
const CALM_SLANT = 0.1;
const GUST = 0.06;
const GUST_MS = 900;
const CALM_SPEED = 0.55;
/** Drops (re)start up to this share of the window's height above the top, so they never fall in step. */
const SPAWN_SPREAD = 0.35;
/** Far to near: further drops are smaller, slower and fainter, and mostly land out of sight. Speeds are px per 60 Hz frame. */
const LAYERS = [
  { drops: 170, calmDrops: 40, speed: 11, length: 12, width: 1, alpha: 0.3, splash: 0 },
  { drops: 120, calmDrops: 28, speed: 16.5, length: 21, width: 1.3, alpha: 0.45, splash: 0.4 },
  { drops: 75, calmDrops: 12, speed: 24, length: 34, width: 1.7, alpha: 0.62, splash: 1 },
];

/** Splash droplets: how many can be in the air at once, their pull back down (px per frame²) and size. */
const DROPLETS = 320;
const CALM_DROPLETS = 40;
const GRAVITY = 0.32;
const DROPLET_SIZE = 1.6;
const DROPLET_ALPHA = 0.55;

/**
 * Roughly when each strike lands (ms), each nudged by STRIKE_JITTER so the
 * rhythm isn't even. They stay over a second apart, so with two flashes a
 * strike it never flashes more than twice in a second.
 */
const STRIKES_AT = [1400, 3050, 4550];
const STRIKE_JITTER = [-150, 250] as const;
/** The leader creeps down from the cloud for this long before the strike lights everything up. */
const LEADER_MS = 90;
const LEADER_ALPHA = 0.4;
/** A strike's light over time (ms, 0–1): bright, a dip, bright again, then a quick decay. */
const FLICKER: Keys = [[0, 0], [18, 1], [50, 0.8], [72, 0.15], [98, 0.2], [120, 0.95], [150, 0.65], [200, 0.28], [300, 0]];
/** Reduced motion: one soft swell and fade. */
const SWELL: Keys = [[0, 0], [110, 1], [260, 0.45], [560, 0]];
/** How strongly the flash whites out the window at full light. */
const FLASH_ALPHA = 0.7;
const CALM_FLASH_ALPHA = 0.2;
const FLASH_COLOR = "#eef3ff";
/** The cloud the bolt comes out of lights up around it. */
const CLOUD_GLOW = ["rgba(232, 238, 255, 0.55)", "rgba(232, 238, 255, 0.18)", "rgba(232, 238, 255, 0)"];
const CALM_CLOUD = 0.5;
/** The bolt outlives the flash a little, fading from BOLT_TAIL over BOLT_MS. */
const BOLT_TAIL = 0.4;
const BOLT_MS = 450;
const CALM_BOLT_ALPHA = 0.7;

/** Segment length (px), how far each zig and the channel's lean swing (radians), and the steepest it goes before it would climb. */
const STEP = [10, 26] as const;
const JAG = 0.55;
const DRIFT = 0.12;
const LEAN = 0.25;
const STEEPEST = 1.3;
/** Per segment, the chance of a branch off the main channel, and of a twig off a branch. */
const BRANCH_CHANCE = [0.13, 0.07];
/** By depth (main channel, branch, twig): core width and brightness. */
const BOLT_WIDTH = [2.4, 1.3, 0.8];
const BOLT_ALPHA = [1, 0.7, 0.45];
/** Wide faint strokes under the core stand in for a blur. */
const BOLT_PASSES = [
  { width: 9, alpha: 0.09, color: "rgb(150, 175, 255)" },
  { width: 3.5, alpha: 0.22, color: "rgb(190, 208, 255)" },
  { width: 1, alpha: 1, color: "#fff" },
];

interface Drop {
  x: number;
  /** The head; the streak trails up behind it. */
  y: number;
  speed: number;
  len: number;
  on: boolean;
  splashed: boolean;
}

interface Layer {
  drops: Drop[];
  /** How many drops are falling. */
  on: number;
  width: number;
  alpha: number;
  /** The chance a drop splashes when it lands. */
  splash: number;
}

interface Droplet {
  x: number;
  y: number;
  vx: number;
  vy: number;
  live: boolean;
}

interface Bolt {
  /** Polylines as flat x, y pairs, by depth. Each one only ever heads down, so it can be cut off at a height. */
  lines: number[][][];
  /** The lowest point, for growing it down from the cloud. */
  bottom: number;
  x: number;
  power: number;
  glow: CanvasGradient;
}

interface Strike {
  at: number;
  bolt: Bolt | null;
  done: boolean;
}

const clamp = (x: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, x));

/** 0 before `a`, 1 after `b`, easing in and out between. */
function smooth(a: number, b: number, x: number) {
  const u = clamp((x - a) / (b - a), 0, 1);
  return u * u * (3 - 2 * u);
}

/** The value of keyframes at `ms`, straight lines between them. */
function level(keys: Keys, ms: number) {
  for (let i = 1; i < keys.length; i++) {
    if (ms <= keys[i][0]) {
      const [t0, v0] = keys[i - 1];
      const [t1, v1] = keys[i];
      return v0 + ((v1 - v0) * (ms - t0)) / (t1 - t0);
    }
  }
  return 0;
}

/** One channel of a bolt, zigzagging down and forking as it goes. The main channel runs down to `length` px; branches run that far. */
function channel(lines: number[][][], x: number, y: number, heading: number, length: number, depth: number, h: number) {
  const pts = [x, y];
  const scale = depth ? 0.7 : 1;
  let lean = heading;
  let run = 0;
  while (depth ? run < length : y < length) {
    const step = rand(STEP[0], STEP[1]) * scale;
    lean = clamp(lean + rand(-DRIFT, DRIFT), heading - LEAN, heading + LEAN);
    const a = clamp(lean + rand(-JAG, JAG), -STEEPEST, STEEPEST);
    x += Math.sin(a) * step;
    y += Math.cos(a) * step;
    run += step;
    pts.push(x, y);
    if (depth < 2 && Math.random() < BRANCH_CHANCE[depth]) {
      const side = Math.random() < 0.5 ? -1 : 1;
      const reach = h * (depth ? rand(0.04, 0.1) : rand(0.1, 0.28));
      channel(lines, x, y, clamp(lean + side * rand(0.4, 0.9), -1.1, 1.1), reach, depth + 1, h);
    }
  }
  lines[depth].push(pts);
}

/** A bolt from above the top edge, away from where the last one struck. */
function makeBolt(ctx: CanvasRenderingContext2D, w: number, h: number, last: number): Bolt {
  let x = rand(0.12, 0.88) * w;
  for (let i = 0; i < 4 && Math.abs(x - last) < w * 0.25; i++) x = rand(0.12, 0.88) * w;
  const end = h * rand(0.6, 0.92);
  const lines: number[][][] = [[], [], []];
  channel(lines, x, -12, rand(-0.2, 0.2), end, 0, h);
  let bottom = 0;
  for (const depth of lines) for (const pts of depth) for (let i = 1; i < pts.length; i += 2) bottom = Math.max(bottom, pts[i]);
  const glow = ctx.createRadialGradient(x, 0, 0, x, 0, Math.max(w, h) * 0.55);
  CLOUD_GLOW.forEach((c, i) => glow.addColorStop(i / (CLOUD_GLOW.length - 1), c));
  return { lines, bottom, x, power: rand(0.8, 1), glow };
}

/** Adds a polyline to the path, cut off where it passes `limit`. */
function trace(ctx: CanvasRenderingContext2D, pts: number[], limit: number) {
  if (pts[1] > limit) return;
  ctx.moveTo(pts[0], pts[1]);
  for (let i = 2; i < pts.length; i += 2) {
    const y = pts[i + 1];
    if (y > limit) {
      const px = pts[i - 2];
      const py = pts[i - 1];
      ctx.lineTo(px + ((pts[i] - px) * (limit - py)) / (y - py), limit);
      return;
    }
    ctx.lineTo(pts[i], y);
  }
}

function drawBolt(ctx: CanvasRenderingContext2D, bolt: Bolt, limit: number, alpha: number) {
  ctx.lineCap = "round";
  for (const pass of BOLT_PASSES) {
    ctx.strokeStyle = pass.color;
    for (let depth = 0; depth < bolt.lines.length; depth++) {
      ctx.beginPath();
      for (const pts of bolt.lines[depth]) trace(ctx, pts, limit);
      ctx.lineWidth = BOLT_WIDTH[depth] * pass.width;
      ctx.globalAlpha = alpha * pass.alpha * BOLT_ALPHA[depth];
      ctx.stroke();
    }
  }
}

export default function play(): Promise<void> {
  const soft = calm();
  const layers: Layer[] = LAYERS.map((l) => ({
    drops: Array.from({ length: soft ? l.calmDrops : l.drops }, () => ({
      x: 0,
      y: 0,
      speed: l.speed * rand(0.9, 1.1) * (soft ? CALM_SPEED : 1),
      len: l.length * rand(0.8, 1.2) * (soft ? 0.8 : 1),
      on: false,
      splashed: false,
    })),
    on: 0,
    width: l.width,
    alpha: l.alpha,
    splash: l.splash,
  }));
  const droplets: Droplet[] = Array.from({ length: soft ? CALM_DROPLETS : DROPLETS }, () => ({ x: 0, y: 0, vx: 0, vy: 0, live: false }));
  /** The oldest droplet, reused first when they're all in the air. */
  let next = 0;
  const strikes: Strike[] = STRIKES_AT.map((at) => ({ at: at + rand(STRIKE_JITTER[0], STRIKE_JITTER[1]), bolt: null, done: false }));
  let lastX = -Infinity;
  const keys = soft ? SWELL : FLICKER;
  const lead = soft ? 0 : LEADER_MS;
  const strikeMs = lead + Math.max(keys[keys.length - 1][0], soft ? 0 : BOLT_MS);
  let sky: CanvasGradient | null = null;
  let skyH = 0;

  const restart = (d: Drop, w: number, h: number, slant: number) => {
    d.x = rand(-slant * h * (1 + SPAWN_SPREAD), w);
    d.y = -d.len - rand(0, h * SPAWN_SPREAD);
    d.splashed = false;
  };

  const splash = (x: number, h: number, near: boolean, slant: number) => {
    const n = soft || !near ? 1 : Math.random() < 0.5 ? 2 : 3;
    for (let i = 0; i < n; i++) {
      const p = droplets[next];
      next = (next + 1) % droplets.length;
      p.x = x;
      p.y = h;
      p.vx = rand(-1.2, 1.2) + slant * 2;
      p.vy = -rand(1.3, 2.6) * (near ? 1 : 0.7) * (soft ? 0.6 : 1);
      p.live = true;
    }
  };

  return scene(({ ctx, w, h, t, dt }) => {
    ctx.lineCap = "butt";
    ctx.lineJoin = "round";

    const dark = smooth(0, DARKEN_MS, t) * (1 - smooth(CLEAR_MS, TOTAL_MS, t));
    if (!sky || skyH !== h) {
      sky = ctx.createLinearGradient(0, 0, 0, h);
      sky.addColorStop(0, SKY_TOP);
      sky.addColorStop(1, SKY_BOTTOM);
      skyH = h;
    }
    ctx.globalAlpha = dark;
    ctx.fillStyle = sky;
    ctx.fillRect(0, 0, w, h);

    const density = smooth(RAIN_IN[0], RAIN_IN[1], t) * (1 - smooth(RAIN_OUT[0], RAIN_OUT[1], t));
    const fade = 1 - smooth(TOTAL_MS - RAIN_FADE_MS, TOTAL_MS, t);
    const slant = soft ? CALM_SLANT : SLANT + GUST * Math.sin(t / GUST_MS);
    const norm = Math.hypot(slant, 1);
    const ux = slant / norm;
    const uy = 1 / norm;

    ctx.strokeStyle = RAIN_COLOR;
    for (let i = 0; i < layers.length; i++) {
      const layer = layers[i];
      const target = Math.round(layer.drops.length * density);
      ctx.beginPath();
      for (const d of layer.drops) {
        if (!d.on) {
          if (layer.on >= target) continue;
          restart(d, w, h, slant);
          d.on = true;
          layer.on++;
        }
        d.x += slant * d.speed * dt;
        d.y += d.speed * dt;
        if (!d.splashed && d.y >= h) {
          d.splashed = true;
          if (Math.random() < layer.splash) splash(d.x - slant * (d.y - h), h, i === LAYERS.length - 1, slant);
        }
        if (d.y - uy * d.len > h) {
          if (layer.on > target) {
            d.on = false;
            layer.on--;
            continue;
          }
          restart(d, w, h, slant);
        }
        ctx.moveTo(d.x, d.y);
        ctx.lineTo(d.x - ux * d.len, d.y - uy * d.len);
      }
      ctx.lineWidth = layer.width;
      ctx.globalAlpha = layer.alpha * fade;
      ctx.stroke();
    }

    ctx.beginPath();
    for (const p of droplets) {
      if (!p.live) continue;
      p.x += p.vx * dt;
      p.y += p.vy * dt + 0.5 * GRAVITY * dt * dt;
      p.vy += GRAVITY * dt;
      if (p.y > h + DROPLET_SIZE) p.live = false;
      else ctx.rect(p.x - DROPLET_SIZE / 2, p.y - DROPLET_SIZE / 2, DROPLET_SIZE, DROPLET_SIZE);
    }
    ctx.fillStyle = RAIN_COLOR;
    ctx.globalAlpha = DROPLET_ALPHA * fade;
    ctx.fill();

    for (const s of strikes) {
      if (s.done || t < s.at) continue;
      const age = t - s.at;
      if (age > strikeMs) {
        s.done = true;
        s.bolt = null;
        continue;
      }
      if (!s.bolt) {
        s.bolt = makeBolt(ctx, w, h, lastX);
        lastX = s.bolt.x;
      }
      const bolt = s.bolt;
      // Since the leader reached the ground and the strike lit up.
      const since = age - lead;
      const light = since < 0 ? 0 : level(keys, since) * bolt.power;
      if (light > 0) {
        ctx.globalAlpha = light * (soft ? CALM_FLASH_ALPHA : FLASH_ALPHA);
        ctx.fillStyle = FLASH_COLOR;
        ctx.fillRect(0, 0, w, h);
        ctx.globalAlpha = light * (soft ? CALM_CLOUD : 1);
        ctx.fillStyle = bolt.glow;
        ctx.fillRect(0, 0, w, h);
      }
      const alpha =
        since < 0 ? LEADER_ALPHA : soft ? light * CALM_BOLT_ALPHA : Math.min(1, light + BOLT_TAIL * Math.max(0, 1 - since / BOLT_MS));
      if (alpha > 0) drawBolt(ctx, bolt, since < 0 ? (bolt.bottom * age) / lead : bolt.bottom, alpha);
    }

    ctx.globalAlpha = 1;
    return t < TOTAL_MS;
  });
}
